import { Capacitor } from "@capacitor/core";
import type {
  RevenueCatPlatform,
  WebPaywallHostHandle,
} from "@um/ui/services/revenueCat";
import { usePaymentsStore } from "@/store/usePaymentsStore";

let cached: RevenueCatPlatform | null = null;
let loading: Promise<RevenueCatPlatform> | null = null;

const WEB_PAYWALL_PANEL_WAIT_MS = 8_000;
let webPaywallHostSessionSeq = 0;

/**
 * Resolves the panel element provided by `PaywallHost`. Web paywall presentation needs
 * a uniqueminds-specific phone-style modal mount, so we own the host wiring here and
 * pass the resolver to the shared web RC platform.
 */
function acquireWebPaywallHost(): Promise<WebPaywallHostHandle> {
  return new Promise((resolve, reject) => {
    const sessionId = ++webPaywallHostSessionSeq;
    const timeoutId = window.setTimeout(() => {
      const cur = usePaymentsStore.getState().webPaywallHostSession;
      if (cur?.sessionId === sessionId) {
        usePaymentsStore.getState().setWebPaywallHostSession(null);
      }
      reject(
        new Error(
          "[payments] Web paywall shell did not mount in time — ensure PaywallHost is rendered."
        )
      );
    }, WEB_PAYWALL_PANEL_WAIT_MS);

    usePaymentsStore.getState().setWebPaywallHostSession({
      sessionId,
      resolvePanel: (el) => {
        window.clearTimeout(timeoutId);
        resolve({
          element: el,
          release: () => {
            const cur = usePaymentsStore.getState().webPaywallHostSession;
            if (cur?.sessionId === sessionId) {
              usePaymentsStore.getState().setWebPaywallHostSession(null);
            }
          },
        });
      },
    });
  });
}

/**
 * Loads the RevenueCat platform for this runtime, importing the SDK on demand.
 *
 * Both implementations are dynamically imported: the web one pulls in
 * `@revenuecat/purchases-js` (~180KB gzipped) and the native one pulls in the
 * Capacitor purchase plugins. Nothing needs either until a signed-in account
 * exists, so keeping them out of the entry chunk is pure win for the /explore
 * ad landing page, which has no account and no paywall.
 *
 * The in-flight promise is cached alongside the instance so concurrent callers
 * during startup share one import and one platform.
 */
export function loadRevenueCatPlatform(): Promise<RevenueCatPlatform> {
  if (cached) return Promise.resolve(cached);
  if (loading) return loading;

  loading = (async () => {
    const platform = Capacitor.isNativePlatform()
      ? (await import("./platformNative")).createNativeRevenueCatPlatform()
      : (
          await import("@um/ui/services/revenueCat/platformWeb")
        ).createWebRevenueCatPlatform({
          acquirePaywallHost: acquireWebPaywallHost,
          setPaywallVisible: (visible) =>
            usePaymentsStore.getState().setIsPaywallVisible(visible),
        });
    cached = platform;
    return platform;
  })();

  return loading;
}

/**
 * The platform instance if it has already been loaded, otherwise null. For
 * callers that must stay synchronous and have nothing to do when RevenueCat
 * was never initialised (teardown, "is a purchase in flight?" checks).
 */
export function peekRevenueCatPlatform(): RevenueCatPlatform | null {
  return cached;
}
